import * as React from "react";

export type ServiceType =
  | "openai"
  | "azure"
  | "openai-compatible"
  | "anthropic"
  | "bedrock"
  | "bedrock-agentcore";
export type MessageSenderType = "system" | "user" | "assistant";
export type MessageStatus = "sending" | "sent" | "error";

export type Attachment = {
  range: string;
  headers: string[];
  columnLetters: string[];
  hasHeaderRow: boolean;
  firstDataRow: number;
  dataCsv: string;
};

export type Message = {
  id?: string;
  status?: MessageStatus;
  error?: string;
  timestamp?: number;
  role: MessageSenderType;
  content: string;
  // Legacy chats may still contain raw CSV strings.
  attachments?: (string | Attachment)[];
  // AI-proposed workbook edits awaiting (or having received) user approval.
  proposals?: EditProposal[];
};

export type EditProposalStatus = "pending" | "applied" | "skipped";

export type EditProposal = {
  id: string;
  address: string;
  operation: "formula" | "value";
  value: string;
  status: EditProposalStatus;
};

export type Chat = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  messages: Message[];
};

export interface Settings {
  autoSave: boolean;
  apiKey: string;
  apiUrl: string;
  selectedService: ServiceType;
  theme: "light" | "dark";
  language: string;
  // Deprecated: use chats/currentChatId instead. Kept for backward-compat migration.
  messages: Message[];
  chats: Chat[];
  currentChatId?: string;
  // Tracks whether the Quick Start Guide has been shown to the user.
  hasSeenQuickStart?: boolean;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  toggleTheme: () => void; // Add this
  isDarkMode: boolean; // Add this
}

const defaultSettings: Settings = {
  autoSave: true,
  apiKey: "",
  apiUrl: "",
  selectedService: "openai",
  theme: "light",
  language: "en-US",
  messages: [],
  chats: [],
  currentChatId: undefined,
};

const SettingsContext = React.createContext<SettingsContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
  toggleTheme: () => {},
  isDarkMode: false,
});

export const useSettings = () => React.useContext(SettingsContext);

interface SettingsProviderProps {
  children: React.ReactNode;
  initialSettings?: Partial<Settings>;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
  initialSettings = {},
}) => {
  const [settings, setSettings] = React.useState<Settings>({
    ...defaultSettings,
    ...initialSettings,
  });

  // Load settings from localStorage on mount and migrate single-chat state if needed
  React.useEffect(() => {
    const savedSettings = localStorage.getItem("excel-ai-settings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings((prev) => {
          const merged = { ...prev, ...parsed } as Settings;
          // Migration: if chats missing but messages exist, create a chat
          if (
            (!merged.chats || merged.chats.length === 0) &&
            Array.isArray(merged.messages) &&
            merged.messages.length > 0
          ) {
            const firstUser = merged.messages.find((m) => m.role === "user");
            const chatName = firstUser ? firstUser.content || "New chat" : "New chat";
            const now = Date.now();
            const chatId = `${now}`;
            merged.chats = [
              {
                id: chatId,
                name: chatName,
                createdAt: now,
                updatedAt: now,
                messages: merged.messages,
              },
            ];
            merged.currentChatId = chatId;
          }
          // Ensure keys exist
          if (!merged.chats) merged.chats = [];
          return merged;
        });
      } catch (error) {
        console.error("Failed to parse saved settings:", error);
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  React.useEffect(() => {
    const toSave = { ...settings } as Settings;
    if (!toSave.autoSave) {
      // If autoSave is off, omit heavy message history for storage, keep metadata
      const { messages: deprecatedMessages, chats, ...rest } = toSave as any;
      const chatsMetaOnly = Array.isArray(chats)
        ? chats.map((c: Chat) => ({ ...c, messages: [] }))
        : [];
      localStorage.setItem("excel-ai-settings", JSON.stringify({ ...rest, chats: chatsMetaOnly }));
    } else {
      localStorage.setItem("excel-ai-settings", JSON.stringify(toSave));
    }
  }, [settings]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  };

  const toggleTheme = () => {
    setSettings((prev) => ({
      ...prev,
      theme: prev.theme === "light" ? "dark" : "light",
    }));
  };

  const isDarkMode = settings.theme === "dark";

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings,
        toggleTheme,
        isDarkMode,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
